<template>
  <div class="mt-2">
    <div class="card p-3 bg-white rounded-xl shadow-sm border border-gray-100">
      <div class="flex items-center justify-between mb-3">
        <div>
          <h2 class="text-base font-bold text-gray-800">Promote Students</h2>
          <p class="text-gray-500 text-xs mt-0.5">Move students to the next academic session or term.</p>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-12 gap-3 relative">
        <!-- Source Filters -->
        <div class="lg:col-span-5 flex flex-col gap-2">
          <div class="bg-gray-50 p-2.5 rounded-lg border border-gray-200 h-full">
            <div class="flex items-center gap-1.5 mb-2 text-gray-700">
              <i class="pi pi-briefcase text-sm"></i>
              <h3 class="font-semibold text-sm">Current Class (Source)</h3>
            </div>

            <div class="flex flex-col gap-2">
              <div>
                <label class="text-xs font-medium text-gray-600 mb-1 block">Academic Calendar</label>
                <AcademicCalendarSelect v-model="selectedAcademicCalendar" placeholder="Select Academic Calendar" />
              </div>
              <div>
                <label class="text-xs font-medium text-gray-600 mb-1 block">Classroom</label>
                <!-- <Select v-model="selectedClassroom" :options="classroomOptions" optionLabel="label"
                  optionValue="value" placeholder="Select Classroom" class="w-full" filter showClear /> -->

                  <ClassroomSectionSelect v-model="selectedClassroom" className="w-full" :showClassroomsOnly="true" />
              </div>

              <div v-if="showSourceSectionSelect">
                <label class="text-xs font-medium text-gray-600 mb-1 block">Section</label>
                <Select v-model="selectedSourceSection" :options="sourceSectionOptions" optionLabel="label"
                  optionValue="value" placeholder="Select Section" class="w-full" />
              </div>

              <div>
                <label class="text-xs font-medium text-gray-600 mb-1 block">Enrollment Status</label>
                <EnrollmentStatusSelect v-model="selectedStatus" data-testid="filter-enrollment-status-promote" />
              </div>

              <div>
                <label class="text-xs font-medium text-gray-600 mb-1 block">Student Name (Optional)</label>
                <IconField iconPosition="left" class="w-full">
                  <InputIcon class="pi pi-search" />
                  <InputText v-model="searchQuery" :placeholder="`Name, Student ID...`" class="w-full text-sm"
                    @keydown.enter="triggerSearch" />
                </IconField>
              </div>

              <!-- Load Students Button -->
              <div class="pt-1">
                <Button label="Load Students" icon="pi pi-refresh" size="small" @click="triggerSearch" :loading="loadingStudents"
                  :disabled="!selectedClassroom || !selectedAcademicCalendar" severity="secondary" class="w-full p-button-sm" outlined />
              </div>
            </div>
          </div>
        </div>

        <!-- Arrow Divider -->
        <div class="lg:col-span-2 flex items-center justify-center py-2 lg:py-0">
          <div class="bg-primary/10 text-primary rounded-full p-2 hidden lg:flex shadow-sm">
            <i class="pi pi-arrow-right text-base"></i>
          </div>
          <div class="bg-primary/10 text-primary rounded-full p-2 flex lg:hidden shadow-sm transform rotate-90">
            <i class="pi pi-arrow-right text-base"></i>
          </div>
        </div>

        <!-- Target Filters -->
        <div class="lg:col-span-5 flex flex-col gap-2">
          <div class="bg-primary/10 p-2.5 rounded-lg border border-primary/20 h-full">
            <div class="flex items-center gap-1.5 mb-2 text-primary-700">
              <i class="pi pi-send text-sm"></i>
              <h3 class="font-semibold text-sm">Promote To (Target)</h3>
            </div>

            <div class="flex flex-col gap-2">
              <div>
                <div class="flex items-center justify-between mb-1">
                   <label class="text-xs font-medium text-primary-800 block">Target Academic Calendar</label>
                   <span @click="showCreateCalendarModal = true" class="text-xs text-primary hover:text-primary-700 underline cursor-pointer">
                     Create New?
                   </span>
                </div>
                <AcademicCalendarSelect v-model="targetAcademicCalendar" placeholder="Select Target Calendar" />
              </div>
              <div>
                <label class="text-xs font-medium text-primary-800 mb-1 block">Target Classroom</label>
                <Select v-model="targetClassroom" :options="classroomOptions" optionLabel="label" optionValue="value"
                  placeholder="Select Target Classroom" class="w-full" filter showClear />
              </div>

              <div v-if="showTargetSectionSelect">
                <label class="text-xs font-medium text-primary-800 mb-1 block">Target Section</label>
                <Select v-model="targetSection" :options="targetSectionOptions" optionLabel="label"
                  optionValue="value" placeholder="Select Target Section" class="w-full" />
              </div>

              <div class="mt-auto pt-2">
                <Button label="Promote Selected Students" icon="pi pi-arrow-right" iconPos="right"
                  @click="onPromoteClick" :loading="promoting" :disabled="!canPromote" severity="primary"
                  class="w-full p-button-sm" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Results Table -->
    <div class="card mt-3 p-1 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <!-- Pagination Controls -->
      <ReusablePrimeTable
        showCheckbox
        :dataKey="'id'"
        :headers="headers"
        :data="filteredStudents"
        v-model:selection="selectedStudents"
        :tableStyle="'min-width: 60rem'"
        :paginator="true"
        :rows="first"
        :totalRecords="totalRecords"
        :lazy="true"
        @page="onPageChange"
      >
        <template #actions="{ row }">
          <Button icon="pi pi-ellipsis-v" class="p-button-text p-button-sm p-button-secondary"
            @click="toggleMenu($event, row)" />
          <Menu ref="menu" :model="getMenuItems(row)" :popup="true" />
        </template>
      </ReusablePrimeTable>
    </div>

    <!-- Create Calendar Modal -->
    <Dialog v-model:visible="showCreateCalendarModal" modal header=" " class="w-full max-w-3xl" :breakpoints="{ '1199px': '75vw', '575px': '90vw' }">
        <CreateAcademicCalendarModal :currentStep="3" @update-step="onCalendarCreated" />
    </Dialog>

  </div>
</template>

<script setup lang="ts">
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';
import { useStudents } from '@/composables/queries/useStudent';
import ReusablePrimeTable from '@/components/common/ReusablePrimeTable.vue';
import { EnrollmentStatusEnum, type Student } from '@/types/composition-functions';
import { useStudentMutations } from '@/composables/mutations/students/useStudentMutations';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import AcademicCalendarSelect from '@/components/common/AcademicCalendarSelect.vue';
import CreateAcademicCalendarModal from '@/components/DashBoard/Modal/CreateAcademicCalendarModal.vue';
import ClassroomSectionSelect from '@/components/common/ClassroomSectionSelect.vue';

// ----- Interfaces -----
interface ClassroomSection {
    id: string;
    section?: {
        id: string;
        name: string;
    } | null;
}

interface Classroom {
  id: string;
  name: string;
  classroomSections?: ClassroomSection[];
}

interface SelectOption {
  label: string;
  value: string;
}

// ----- Refs for filters -----
const selectedAcademicCalendar = ref<string | undefined>(undefined);
const selectedClassroom = ref<string | undefined>(undefined);
const selectedSourceSection = ref<string | undefined>(undefined);
const selectedStatus = ref<EnrollmentStatusEnum | null>(EnrollmentStatusEnum.Enrolled);
const searchQuery = ref('');

// ----- Target Filters -----
const targetAcademicCalendar = ref<string | undefined>(undefined);
const targetClassroom = ref<string | undefined>(undefined);
const targetSection = ref<string | undefined>(undefined);
const promoting = ref(false);
const showCreateCalendarModal = ref(false);

// ----- Pagination State -----
const page = ref(1);
const first = ref(50); // Rows per page

const { editStudent, promoteStudents } = useStudentMutations();
const toast = useToast();
const confirm = useConfirm();

// ----- Queries -----
const { classrooms, refetchSchool } = useSchoolQuery();

// ----- Dependent Options Logic (Source) -----
const sourceSectionOptions = computed<SelectOption[]>(() => {
  if (!selectedClassroom.value) return [];

  // Check if classrooms are available
  if (!classrooms.value) return [];

  // Cast classrooms.value to Classroom[] safely
  const allClasses = classrooms.value as unknown as Classroom[];

  const cls = allClasses.find((c: Classroom) => c.id === selectedClassroom.value);
  if (!cls || !cls.classroomSections) return [];

  // Sort sections alphabetically if needed, but here we just map
  return cls.classroomSections.map((cs: ClassroomSection) => ({
    label: cs.section?.name || 'Main', // Fallback for display
    value: cs.id,
  }));
});

const showSourceSectionSelect = computed(() => {
  if (!selectedClassroom.value) return false;
  if (!classrooms.value) return false;
  const allClasses = classrooms.value as unknown as Classroom[];

  const cls = allClasses.find((c: Classroom) => c.id === selectedClassroom.value);
  // Logic: If sections exist AND at least one section has a NAME
  if (cls && cls.classroomSections && cls.classroomSections.length > 0) {
    // If there's only one section and its section.name is null/undefined/empty, hide it
    if (cls.classroomSections.length === 1 && !cls.classroomSections?.[0]?.section?.name) {
      return false;
    }
    return true;
  }
  return false;
});

// Auto-select source section if hidden or only one option
watch(selectedClassroom, (newVal) => {
  selectedSourceSection.value = undefined; // Reset first

  if (newVal && classrooms.value) {
    const allClasses = classrooms.value as unknown as Classroom[];
    const cls = allClasses.find((c: Classroom) => c.id === newVal);

    if (cls && cls.classroomSections && cls.classroomSections.length > 0) {
      // If we are NOT showing the select, we must pick the first available ID
      if (!showSourceSectionSelect.value) {
        selectedSourceSection.value = cls.classroomSections?.[0]?.id;
      }
    }
  }
});

// ----- Dependent Options Logic (Target) -----
const targetSectionOptions = computed<SelectOption[]>(() => {
  if (!targetClassroom.value) return [];
  if (!classrooms.value) return [];
  const allClasses = classrooms.value as unknown as Classroom[];

  const cls = allClasses.find((c: Classroom) => c.id === targetClassroom.value);
  if (!cls || !cls.classroomSections) return [];

  return cls.classroomSections.map((cs: ClassroomSection) => ({
    label: cs.section?.name || 'Main',
    value: cs.id,
  }));
});

const showTargetSectionSelect = computed(() => {
  if (!targetClassroom.value) return false;
  if (!classrooms.value) return false;
  const allClasses = classrooms.value as unknown as Classroom[];

  const cls = allClasses.find((c: Classroom) => c.id === targetClassroom.value);
  if (cls && cls.classroomSections && cls.classroomSections.length > 0) {
    if (cls.classroomSections.length === 1 && !cls.classroomSections?.[0]?.section?.name) {
      return false;
    }
    return true;
  }
  return false;
});

// Auto-select target section
watch(targetClassroom, (newVal) => {
  targetSection.value = undefined;

  if (newVal && classrooms.value) {
    const allClasses = classrooms.value as unknown as Classroom[];
    const cls = allClasses.find((c: Classroom) => c.id === newVal);

    if (cls && cls.classroomSections && cls.classroomSections.length > 0) {
      if (!showTargetSectionSelect.value) {
        targetSection.value = cls.classroomSections?.[0]?.id;
      }
    }
  }
});

// ----- Students Fetching -----
const { students: fetchedStudents, refetchStudents, loadStudents } = useStudents({
  getStudentsOptions: { autoFetch: false },
  studentFilters: computed(() => {
    let sectionIds: string[] | undefined = undefined;

    if (selectedSourceSection.value) {
      sectionIds = [selectedSourceSection.value];
    } else if (selectedClassroom.value && classrooms.value) {
      // If classroom selected but no section, include ALL sections of that classroom
      const allClasses = classrooms.value as unknown as Classroom[];
      const cls = allClasses.find((c: Classroom) => c.id === selectedClassroom.value);
      if (cls && cls.classroomSections) {
        sectionIds = cls.classroomSections.map((cs) => cs.id);
      }
    }

    return {
      page: page.value,
      first: first.value,
      filter: {
        classroom_section_ids: sectionIds,
        academic_calendar_id: selectedAcademicCalendar.value,
        status: selectedStatus.value || undefined,
      },
    };
  }),
});

const paginatorInfo = computed(() => fetchedStudents.paginatorInfo.value);
const totalRecords = computed(() => paginatorInfo.value?.total || 0);
const loadingStudents = fetchedStudents.loading; // Exposed for button state

// ----- Computed options (Global) -----
const classroomOptions = computed<SelectOption[]>(() =>
  (classrooms.value as unknown as Classroom[] | undefined)?.map(c => ({
    label: c.name,
    value: c.id, // Now using Classroom ID, not section
  })) || [],
);

// ----- Students table -----
interface StudentRow {
  id: string;
  firstname: string;
  surname?: string | null;
  gender?: string | null;
  class?: string;
  className?: string; // Add className prop for display
  term: string;
  email?: string | null;
  actions?: string;
  originalStudent: Student; // Keep reference to original student
}

const students = ref<StudentRow[]>([]);
const selectedStudents = ref<StudentRow[]>([]); // For table selection

const headers = [
  { field: 'id', header: 'Student ID' },
  { field: 'firstname', header: 'First name' },
  { field: 'surname', header: 'Surname' },
  { field: 'gender', header: 'Gender' },
  { field: 'className', header: 'Class' },
];

// ----- Map fetched students to table rows -----
watch(
  () => fetchedStudents.data.value,
  (newStudents) => {
    if (newStudents) {
      students.value = newStudents.map((s: Student) => ({
        id: s.matric_no,
        firstname: s.first_name,
        surname: s.surname,
        gender: s.gender,
        class: s.currentClassroomSection?.classroom?.id,
        className: s.currentClassroomSection?.classroom?.name,
        term: 'N/A',
        email: s.email,
        actions: '',
        originalStudent: s,
      }));
    }
  },
  { immediate: true },
);

// ----- Filtered students -----
const filteredStudents = computed(() =>
  students.value.filter(s => {
    const matchesName =
      !searchQuery.value ||
      s.firstname.toLowerCase().includes(searchQuery.value.trim().toLowerCase()) ||
      s.surname?.toLowerCase().includes(searchQuery.value.trim().toLowerCase());
    return matchesName;
  }));

// ----- Search & Pagination Handlers -----
function triggerSearch() {
  page.value = 1;
  loadStudents();
}

function onPageChange(event: { page: number; rows: number }) {
  page.value = event.page + 1;
  first.value = event.rows;
  loadStudents();
}

// ----- Menu -----
type MenuRef = { toggle: (event: Event) => void };
const menu = ref<MenuRef | null>(null);

function toggleMenu(event: Event, row: Student) {
  menu.value?.toggle(event);
}

interface MenuItem {
  label: string;
  icon: string;
  command: () => void;
}

function getMenuItems(row: Student): MenuItem[] {
  return [
    { label: 'View', icon: 'pi pi-eye', command: () => viewRow(row) },
    { label: 'Edit', icon: 'pi pi-pencil', command: () => editRow(row) },
    { label: 'Delete', icon: 'pi pi-trash', command: () => deleteRow(row) },
  ];
}

function viewRow(row: Student) { /* ... */ }
function editRow(row: Student) { /* ... */ }
function deleteRow(row: Student) { /* ... */ }

// ----- Promote Logic -----
const canPromote = computed(() =>
// Valid target section is required
  targetAcademicCalendar.value && targetSection.value && selectedStudents.value.length > 0,
);

async function onPromoteClick() {
  if (!canPromote.value) return;

  confirm.require({
    message: `Are you sure you want to promote ${selectedStudents.value.length} students?`,
    header: 'Confirmation',
    icon: 'pi pi-exclamation-triangle',
    accept: async() => {
      promote();
    },
  });
}

async function promote() {
  promoting.value = true;
  try {
    const targetCalendarId = targetAcademicCalendar.value;
    const targetSectionId = targetSection.value;
    const sourceCalendarId = selectedAcademicCalendar.value;

    if (!targetCalendarId || !targetSectionId || !sourceCalendarId) {
      toast.add({ severity: 'error', summary: 'Error', detail: 'Invalid details provided', life: 3000 });
      return;
    }

    const studentIds = selectedStudents.value.map(s => s.originalStudent.id);

    const success = await promoteStudents({
      classroom_section_id: targetSectionId,
      from_academic_calendar_id: sourceCalendarId,
      to_academic_calendar_id: targetCalendarId,
      studentIds: studentIds,
    });

    if (success) {
      // Clear selection and refetch
      selectedStudents.value = [];
      // We don't need to refetch here if promoteStudents already does it,
      // but useStudentMutations' promoteStudents calls loadStudents() on success.
      // If loadStudents() just fetches current state, it might be fine.
    }

  } catch (e: unknown) {
    // Toast is handled in useStudentMutations
  } finally {
    promoting.value = false;
  }
}

// ----- Modal Handler -----
async function onCalendarCreated() {
  showCreateCalendarModal.value = false;
  toast.add({ severity: 'success', summary: 'Success', detail: 'New Academic Calendar Created', life: 3000 });
  await refetchSchool();
}

// ----- Watch for classroom changes -----
watch(
  () => classrooms.value,
  async() => {
    await refetchSchool();
  },
);
</script>

<style lang="scss" scoped></style>
